import type { Voorwaarde } from '../../gedeeld/types'

/* ===========================================================================
 *  Wat je in de verkenner bekijkt, in de adresbalk
 *
 *  Alles: de soort, de bv's, de periode, het zoekwoord, de filters, de
 *  kolommen, de sortering en de draaitabel. Daardoor is doorklikken een
 *  gewone link (van een saldo in het grootboek naar de regels erachter), en
 *  is elke weergave door te sturen: "kijk hier eens naar" is een adres.
 *
 *  `bv` in het adres gaat voor de keuze rechtsboven. Wie vanuit Venlo
 *  doorklikt, wil de regels van Venlo zien -- niet die van alle bv's die hij
 *  toevallig aan heeft staan.
 * =========================================================================== */

export interface Weergave {
  soort: string
  bvs: string[] | null
  tekst: string
  vanaf: string
  tot: string
  voorwaarden: Voorwaarde[]
  kolommen: string[] | null
  sorteer: string | null
  richting: 'op' | 'af'
  draai: { rijen: string; kolommen: string | null; waarde: string } | null
}

export function leesWeergave(soort: string, q: URLSearchParams): Weergave {
  let voorwaarden: Voorwaarde[] = []
  try {
    const f = q.get('f')
    if (f) {
      const w = JSON.parse(f)
      if (Array.isArray(w)) voorwaarden = w.filter((x) => x && typeof x.veld === 'string' && typeof x.op === 'string')
    }
  } catch { /* een kapot adres: dan zonder filters */ }
  const d = q.get('d')
  let draai: Weergave['draai'] = null
  if (d) {
    const [rijen, kolommen, waarde] = d.split('~')
    if (rijen) draai = { rijen, kolommen: kolommen || null, waarde: waarde || '@bedrag' }
  }
  const bv = q.get('bv')
  return {
    soort,
    bvs: bv ? bv.split(',').filter(Boolean) : null,
    tekst: q.get('q') ?? '',
    vanaf: q.get('van') ?? '',
    tot: q.get('tot') ?? '',
    voorwaarden,
    kolommen: q.get('k') ? q.get('k')!.split(',').filter(Boolean) : null,
    sorteer: q.get('o'),
    richting: q.get('r') === 'op' ? 'op' : 'af',
    draai,
  }
}

export function verkennerAdres(w: Partial<Weergave> & { soort: string }): string {
  const q = new URLSearchParams()
  if (w.bvs && w.bvs.length) q.set('bv', w.bvs.join(','))
  if (w.tekst) q.set('q', w.tekst)
  if (w.vanaf) q.set('van', w.vanaf)
  if (w.tot) q.set('tot', w.tot)
  if (w.voorwaarden && w.voorwaarden.length) q.set('f', JSON.stringify(w.voorwaarden))
  if (w.kolommen && w.kolommen.length) q.set('k', w.kolommen.join(','))
  if (w.sorteer) q.set('o', w.sorteer)
  if (w.richting === 'op') q.set('r', 'op')
  if (w.draai) q.set('d', [w.draai.rijen, w.draai.kolommen ?? '', w.draai.waarde].join('~'))
  const s = q.toString()
  return `/v/${encodeURIComponent(w.soort)}${s ? '?' + s : ''}`
}

/** yyyy-mm-dd → epoch-ms (middernacht UTC, zoals Exact datums bewaart). */
export function datumMs(w: string, eindVanDag = false): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(w)) return null
  const t = Date.parse(w + 'T00:00:00Z')
  return Number.isFinite(t) ? t + (eindVanDag ? 86399999 : 0) : null
}
